import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Select } from 'primeng/select';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { InputText } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { autoFitColumns, addAutoFilter } from '../../utils/excel-export.util';
import { OutingService, OutingFilters } from '../../services/outing.service';
import { ProjectService } from '../../services/project.service';
import { EmployeeService } from '../../services/employee.service';
import { Employee } from '../../models/employee.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import {
  Outing,
  OutingStatus,
  OutingReportSummary,
  OUTING_STATUS_LABELS
} from '../../models/outing.model';
import { Project } from '../../models/project.model';

interface DropdownOption {
  label: string;
  value: string | number | null;
}

// One employee grouped across the outings (within filters) they participated in.
interface ParticipationRow {
  employeeId: string;
  employeeName: string;
  email?: string;
  serviceGroupId?: string;
  serviceGroupName?: string;
  goneCount: number;
  outings: {
    eventName: string;
    projectName: string;
    pmName: string;
    plannedDate: string;
    startTime?: string;
    location?: string;
    status: OutingStatus;
    participantCount: number;
    photosFolderUrl?: string;
  }[];
}

// A person on one outing's invite list, with the outcome the PM recorded.
interface AttendeeCell {
  employeeId: string;
  employeeName: string;
  serviceGroupId?: string;
  serviceGroupName?: string;
  email?: string;
  confirmed: boolean;   // replied to the RSVP link (intent only)
}

// Level 2 of the project-wise view: one Done outing, split into who participated and who didn't.
interface OutingBreakdown {
  outingId: string;
  eventName: string;
  pmName: string;
  plannedDate: string;
  startTime?: string;
  location?: string;
  status: OutingStatus;
  photosFolderUrl?: string;
  invitedCount: number;
  participated: AttendeeCell[];
  absent: AttendeeCell[];
}

// Level 1 of the project-wise view: a project with its outings rolled up.
interface ProjectBreakdown {
  projectId: string;
  projectName: string;
  projectCode?: string;
  outingCount: number;
  invitedCount: number;
  participatedCount: number;
  absentCount: number;
  participationRate: number;
  outings: OutingBreakdown[];
}

// Read-only Outing Summary report — stat cards + Participated / Not-participated employee tabs
// (tabs sit on one line with the quarter strip); expand a participated employee to see their outings.
@Component({
  selector: 'app-outing-summary',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, Select, Tag, Tooltip, InputText],
  providers: [MessageService],
  templateUrl: './outing-summary.component.html',
  styleUrl: './outing-summary.component.scss'
})
export class OutingSummaryComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  report: OutingReportSummary | null = null;
  outings: Outing[] = [];
  projects: Project[] = [];
  employees: Employee[] = [];
  loading = false;

  // Server filters
  filterYear: number | null = new Date().getFullYear();
  filterQuarter: number | null = null;
  filterProjectId: string | null = null;
  // Client filters (applied to the employee lists)
  filterServiceGroupId: string | null = null;
  filterCountBucket: string | null = null;
  nameSearch = '';

  yearOptions: DropdownOption[] = [];
  quarterOptions: DropdownOption[] = [
    { label: 'All quarters', value: null },
    { label: 'Q1 (Jan–Mar)', value: 1 },
    { label: 'Q2 (Apr–Jun)', value: 2 },
    { label: 'Q3 (Jul–Sep)', value: 3 },
    { label: 'Q4 (Oct–Dec)', value: 4 }
  ];
  projectFilterOptions: DropdownOption[] = [];
  serviceGroupOptions: DropdownOption[] = [{ label: 'All service groups', value: null }];
  countBucketOptions: DropdownOption[] = [
    { label: 'Any', value: null },
    { label: 'Not participated (0)', value: '0' },
    { label: '1 participated', value: '1' },
    { label: '2 participated', value: '2' },
    { label: '3 or more', value: '3+' }
  ];

  // 'participated'    — employees who attended ≥1 done outing
  // 'notparticipated' — project → outing → the invited-but-absent people (the PM's actionable view)
  // 'neverinvited'    — active employees never put on any done outing's list at all
  participationTab: 'participated' | 'notparticipated' | 'neverinvited' = 'participated';
  expandedKeys: { [employeeId: string]: boolean } = {};
  // Independent expansion state for the project-wise tree (projects and outings nest).
  expandedProjects: { [projectId: string]: boolean } = {};
  expandedOutings: { [outingId: string]: boolean } = {};

  readonly OutingStatus = OutingStatus;

  constructor(
    private outingService: OutingService,
    private projectService: ProjectService,
    private employeeService: EmployeeService,
    private messageService: MessageService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.exportCurrentTab();
    });

    this.buildYearOptions();
    forkJoin({
      projects: this.projectService.getAllProjects().pipe(catchError(() => of([] as Project[]))),
      employees: this.employeeService.getAllEmployees().pipe(catchError(() => of([] as Employee[])))
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ projects, employees }) => {
        this.projects = (projects || []).filter(p => p.isActive !== false);
        this.projectFilterOptions = [
          { label: 'All projects', value: null },
          ...this.projects.map(p => ({ label: p.name, value: p.id }))
        ];
        this.employees = (employees || []).filter(e => e.isActive !== false);
        this.buildServiceGroupOptions();
      });
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  private buildYearOptions(): void {
    const current = new Date().getFullYear();
    this.yearOptions = [{ label: 'All years', value: null }];
    for (let y = current + 1; y >= current - 3; y--) {
      this.yearOptions.push({ label: `${y}`, value: y });
    }
  }

  private filters(): OutingFilters {
    return {
      year: this.filterYear ?? undefined,
      quarter: this.filterQuarter ?? undefined,
      projectId: this.filterProjectId ?? undefined
    };
  }

  load(): void {
    this.loading = true;
    forkJoin({
      report: this.outingService.getReport(this.filters()).pipe(catchError(() => of(null))),
      outings: this.outingService.getAll(this.filters()).pipe(catchError(() => of([] as Outing[])))
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ report, outings }) => {
          this.report = report;
          this.outings = outings;
          this.loading = false;
        },
        error: () => { this.loading = false; }
      });
  }

  onFilterChange(): void {
    this.load();
  }

  hasActiveFilters(): boolean {
    return this.filterQuarter != null || this.filterProjectId != null
      || this.filterServiceGroupId != null || this.filterCountBucket != null
      || !!this.nameSearch.trim() || this.filterYear !== new Date().getFullYear();
  }

  clearFilters(): void {
    this.filterYear = new Date().getFullYear();
    this.filterQuarter = null;
    this.filterProjectId = null;
    this.filterServiceGroupId = null;
    this.filterCountBucket = null;
    this.nameSearch = '';
    this.load();
  }

  toggleExpand(employeeId: string): void {
    if (this.expandedKeys[employeeId]) {
      delete this.expandedKeys[employeeId];
    } else {
      this.expandedKeys[employeeId] = true;
    }
    this.expandedKeys = { ...this.expandedKeys };
  }

  // Distinct service groups across all active employees (for the filter).
  private buildServiceGroupOptions(): void {
    const seen = new Map<string, string>();
    for (const e of this.employees) {
      if (e.serviceGroupId && e.serviceGroupName && !seen.has(e.serviceGroupId)) {
        seen.set(e.serviceGroupId, e.serviceGroupName);
      }
    }
    this.serviceGroupOptions = [
      { label: 'All service groups', value: null },
      ...Array.from(seen.entries())
        .map(([value, label]) => ({ label, value }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
  }

  // Participation count per employee = DONE outings where the PM recorded them as having attended.
  // Being on the invite list is NOT participation: the PM confirms actual attendance in the
  // mark-done / update-attendance popup. RSVP is never consulted — it's intent, not attendance.
  private participationCountMap(): Map<string, number> {
    const map = new Map<string, number>();
    for (const o of this.outings) {
      if (o.status !== OutingStatus.Done) continue;
      for (const p of o.participants) {
        if (!p.attended) continue;
        map.set(p.employeeId, (map.get(p.employeeId) ?? 0) + 1);
      }
    }
    return map;
  }

  // How many DONE outings this employee was invited to (regardless of whether they turned up).
  // Pairs with goneCount so the Participated tab can show "4 of 6" rather than a bare count.
  invitedCountFor(employeeId: string): number {
    return this.outings
      .filter(o => o.status === OutingStatus.Done)
      .filter(o => o.participants.some(p => p.employeeId === employeeId))
      .length;
  }

  // Invited-but-absent count for one employee across the filtered set.
  missedCountFor(employeeId: string, goneCount: number): number {
    return Math.max(0, this.invitedCountFor(employeeId) - goneCount);
  }

  // Per-employee participation rate: attended / invited, as a percentage.
  participationRateFor(employeeId: string, goneCount: number): number {
    const invited = this.invitedCountFor(employeeId);
    return invited > 0 ? Math.round((goneCount / invited) * 1000) / 10 : 0;
  }

  // The DONE outings this employee was invited to but did NOT attend, shaped like the participated
  // list so the expansion panel can render both with the same inner table.
  missedOutingsFor(employeeId: string): ParticipationRow['outings'] {
    return this.outings
      .filter(o => o.status === OutingStatus.Done)
      .filter(o => o.participants.some(p => p.employeeId === employeeId && !p.attended))
      .map(o => ({
        eventName: o.eventName,
        projectName: o.projectName,
        pmName: o.pmName,
        plannedDate: o.plannedDate,
        startTime: o.startTime,
        location: o.location,
        status: o.status,
        participantCount: o.participants.length,
        photosFolderUrl: o.photosFolderUrl
      }))
      .sort((a, b) => +new Date(b.plannedDate) - +new Date(a.plannedDate));
  }

  // Did this employee reply yes to the RSVP for an outing they then missed? Worth surfacing —
  // "said yes and still skipped" is different from "never replied".
  rsvpConfirmedForMissed(employeeId: string, eventName: string): boolean {
    const outing = this.outings.find(o => o.eventName === eventName
      && o.participants.some(p => p.employeeId === employeeId && !p.attended));
    return outing?.participants.find(p => p.employeeId === employeeId)?.confirmed ?? false;
  }

  private matchesBucket(count: number): boolean {
    switch (this.filterCountBucket) {
      case '0': return count === 0;
      case '1': return count === 1;
      case '2': return count === 2;
      case '3+': return count >= 3;
      default: return true;
    }
  }

  private matchesFilters(serviceGroupId: string | undefined, name: string, count: number): boolean {
    if (this.filterServiceGroupId && serviceGroupId !== this.filterServiceGroupId) return false;
    if (this.nameSearch.trim() && !name.toLowerCase().includes(this.nameSearch.trim().toLowerCase())) return false;
    return this.matchesBucket(count);
  }

  // Participated = employees the PM recorded as having attended ≥1 done outing, with those outings.
  get participatedRows(): ParticipationRow[] {
    const counts = this.participationCountMap();
    const byEmployee = new Map<string, ParticipationRow>();
    for (const o of this.outings) {
      if (o.status !== OutingStatus.Done) continue;
      for (const p of o.participants) {
        if (!p.attended) continue;
        let row = byEmployee.get(p.employeeId);
        if (!row) {
          row = {
            employeeId: p.employeeId,
            employeeName: p.employeeName,
            email: p.email,
            serviceGroupId: p.serviceGroupId,
            serviceGroupName: p.serviceGroupName,
            goneCount: counts.get(p.employeeId) ?? 0,
            outings: []
          };
          byEmployee.set(p.employeeId, row);
        }
        row.outings.push({
          eventName: o.eventName,
          projectName: o.projectName,
          pmName: o.pmName,
          plannedDate: o.plannedDate,
          startTime: o.startTime,
          location: o.location,
          status: o.status,
          participantCount: o.participants.length,
          photosFolderUrl: o.photosFolderUrl
        });
      }
    }
    return Array.from(byEmployee.values())
      .filter(r => this.matchesFilters(r.serviceGroupId, r.employeeName, r.goneCount))
      .sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }

  // Does this person pass the service-group / name filters? (The participation-count bucket is a
  // per-employee measure and doesn't apply to a person listed inside one specific outing.)
  private matchesPersonFilters(serviceGroupId: string | undefined, name: string): boolean {
    if (this.filterServiceGroupId && serviceGroupId !== this.filterServiceGroupId) return false;
    if (this.nameSearch.trim() && !name.toLowerCase().includes(this.nameSearch.trim().toLowerCase())) return false;
    return true;
  }

  private toAttendeeCell(p: Outing['participants'][number]): AttendeeCell {
    return {
      employeeId: p.employeeId,
      employeeName: p.employeeName,
      serviceGroupId: p.serviceGroupId,
      serviceGroupName: p.serviceGroupName,
      email: p.email,
      confirmed: p.confirmed
    };
  }

  // "Invited but did not participate", grouped project → outing → people.
  // Only Done outings can have absentees: a Planning outing hasn't happened yet, and a Cancelled
  // one never did. Outings with nobody absent are dropped, and so are projects left with no
  // outings — the tab is meant to show only what needs attention.
  get projectBreakdownRows(): ProjectBreakdown[] {
    const byProject = new Map<string, ProjectBreakdown>();

    for (const o of this.outings) {
      if (o.status !== OutingStatus.Done) continue;

      const participated = o.participants
        .filter(p => p.attended)
        .map(p => this.toAttendeeCell(p))
        .sort((a, b) => a.employeeName.localeCompare(b.employeeName));
      const absentAll = o.participants.filter(p => !p.attended);

      // Filters narrow WHO is listed as absent, but the participated side stays whole so the
      // counts still read as "3 of 12 missed it" rather than "3 of 3".
      const absent = absentAll
        .filter(p => this.matchesPersonFilters(p.serviceGroupId, p.employeeName))
        .map(p => this.toAttendeeCell(p))
        .sort((a, b) => a.employeeName.localeCompare(b.employeeName));

      if (!absent.length) continue;

      let project = byProject.get(o.projectId);
      if (!project) {
        project = {
          projectId: o.projectId,
          projectName: o.projectName,
          projectCode: o.projectCode,
          outingCount: 0,
          invitedCount: 0,
          participatedCount: 0,
          absentCount: 0,
          participationRate: 0,
          outings: []
        };
        byProject.set(o.projectId, project);
      }

      project.outings.push({
        outingId: o.id,
        eventName: o.eventName,
        pmName: o.pmName,
        plannedDate: o.plannedDate,
        startTime: o.startTime,
        location: o.location,
        status: o.status,
        photosFolderUrl: o.photosFolderUrl,
        invitedCount: o.participants.length,
        participated,
        absent
      });

      project.outingCount++;
      project.invitedCount += o.participants.length;
      project.participatedCount += participated.length;
      project.absentCount += absentAll.length;
    }

    const rows = Array.from(byProject.values());
    for (const p of rows) {
      p.participationRate = p.invitedCount > 0
        ? Math.round((p.participatedCount / p.invitedCount) * 1000) / 10
        : 0;
      // Most recent outing first within a project.
      p.outings.sort((a, b) => +new Date(b.plannedDate) - +new Date(a.plannedDate));
    }
    // Worst offenders first — the projects with the most no-shows are the ones to act on.
    return rows.sort((a, b) => b.absentCount - a.absentCount || a.projectName.localeCompare(b.projectName));
  }

  // Total absentee rows across the tree (drives the tab's badge count).
  get totalAbsentCount(): number {
    return this.projectBreakdownRows.reduce(
      (sum, p) => sum + p.outings.reduce((s, o) => s + o.absent.length, 0), 0);
  }

  // Never invited = active employees who were never put on ANY done outing's list in the filtered
  // set. Distinct from "invited but didn't participate" — nobody asked them in the first place.
  get neverInvitedRows(): ParticipationRow[] {
    const invitedIds = new Set<string>();
    for (const o of this.outings) {
      if (o.status !== OutingStatus.Done) continue;
      for (const p of o.participants) {
        invitedIds.add(p.employeeId);
      }
    }
    return this.employees
      .filter(e => !invitedIds.has(e.id))
      .map(e => ({
        employeeId: e.id,
        employeeName: e.name,
        email: e.email,
        serviceGroupId: e.serviceGroupId,
        serviceGroupName: e.serviceGroupName,
        goneCount: 0,
        outings: []
      } as ParticipationRow))
      .filter(r => this.matchesFilters(r.serviceGroupId, r.employeeName, r.goneCount))
      .sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }

  toggleProject(projectId: string): void {
    if (this.expandedProjects[projectId]) {
      delete this.expandedProjects[projectId];
    } else {
      this.expandedProjects[projectId] = true;
    }
    this.expandedProjects = { ...this.expandedProjects };
  }

  toggleOuting(outingId: string): void {
    if (this.expandedOutings[outingId]) {
      delete this.expandedOutings[outingId];
    } else {
      this.expandedOutings[outingId] = true;
    }
    this.expandedOutings = { ...this.expandedOutings };
  }

  statusSeverity(status: OutingStatus): 'info' | 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case OutingStatus.Planning: return 'info';
      case OutingStatus.Done: return 'success';
      case OutingStatus.Cancelled: return 'danger';
      default: return 'secondary';
    }
  }

  statusLabel(status: OutingStatus): string {
    return OUTING_STATUS_LABELS[status];
  }

  // Exports whichever tab is open. The project-wise tab flattens to one row per absent person per
  // outing, so the spreadsheet is filterable/pivotable rather than a nested tree.
  exportCurrentTab(): void {
    let data: Record<string, string | number>[];
    let sheet: string;
    let file: string;

    if (this.participationTab === 'notparticipated') {
      data = this.projectBreakdownRows.flatMap(p =>
        p.outings.flatMap(o =>
          o.absent.map(a => ({
            'Project': p.projectName,
            'Project Code': p.projectCode ?? '-',
            'Outing': o.eventName,
            'Date': new Date(o.plannedDate).toLocaleDateString(),
            'PM': o.pmName,
            'Employee': a.employeeName,
            'Service Group': a.serviceGroupName ?? '-',
            'Email': a.email ?? '-',
            'RSVP': a.confirmed ? 'Replied yes' : 'No reply',
            'Invited': o.invitedCount,
            'Participated': o.participated.length
          }))));
      sheet = 'Invited not participated';
      file = 'Outing_InvitedNotParticipated.xlsx';
    } else if (this.participationTab === 'participated') {
      // Mirrors the on-screen columns (Invited / Participated / Missed / rate) and spells out BOTH
      // event lists, so the sheet answers the same questions as the expanded row.
      data = this.participatedRows.map(r => {
        const invited = this.invitedCountFor(r.employeeId);
        const missed = this.missedOutingsFor(r.employeeId);
        return {
          'Employee': r.employeeName,
          'Service Group': r.serviceGroupName ?? '-',
          'Email': r.email ?? '-',
          'Invited': invited,
          'Participated': r.goneCount,
          'Missed': missed.length,
          'Participation %': this.participationRateFor(r.employeeId, r.goneCount),
          'Attended events': r.outings.map(o => `${o.eventName} (${o.projectName})`).join('; ') || '-',
          'Not attended events': missed.map(o => `${o.eventName} (${o.projectName})`).join('; ') || '-'
        };
      });
      sheet = 'Participation';
      file = 'Outing_Participation.xlsx';
    } else {
      data = this.neverInvitedRows.map(r => ({
        'Employee': r.employeeName,
        'Service Group': r.serviceGroupName ?? '-',
        'Email': r.email ?? '-'
      }));
      sheet = 'Never invited';
      file = 'Outing_NeverInvited.xlsx';
    }

    if (!data.length) {
      this.messageService.add({ severity: 'warn', summary: 'Nothing to export', detail: 'No rows in this list.' });
      return;
    }

    const ws = XLSX.utils.json_to_sheet(data);
    // Fit the columns and add filter dropdowns, so the sheet is usable without manual setup.
    autoFitColumns(ws, data);
    addAutoFilter(ws);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheet);
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), file);
  }
}
